import { toast } from "@/design/composables/use-toast";
import { type HttpError, request } from "@/shared/http/client";
import { i18n } from "@/shared/i18n";
import type { PackPurchase } from "./billing.types";
import { stripeWith } from "./stripe-js";

/**
 * CONCLUIR O 3-D SECURE DE UMA COMPRA DE PACOTE.
 *
 * ── ONDE ISTO VIVE, E POR QUE NÃO NUM COMPONENTE ──────────────────────────
 *
 * A autenticação não desenha nada nosso: o Stripe.js abre a sua própria janela
 * sobre a página e fecha-a sozinho. O que fica para nós é uma sequência —
 * carregar a biblioteca, correr o desafio, avisar o servidor, ler o que ele
 * apurou —, e uma sequência não é um componente. Vivendo aqui, ela acontece
 * igual venha a compra de onde vier: do catálogo de pacotes, de um botão futuro,
 * de qualquer sítio que compre um pacote.
 *
 * ── O SERVIDOR É QUEM DIZ SE ENTROU ───────────────────────────────────────
 *
 * `handleCardAction` responde com o estado que o BANCO deu, e é tentador
 * acreditar nele. Não se acredita: o que sai daqui para o servidor é "o desafio
 * fechou, vá ver", e é a Stripe, perguntada pelo servidor, que decide se os
 * créditos entram. Um cliente que pudesse afirmar "autenticou" seria um cliente
 * que credita 2.000 créditos dizendo uma palavra.
 *
 * ── E SEM CHAVE PUBLICÁVEL, NADA DISTO ACONTECE ───────────────────────────
 *
 * Uma instalação sem `publishableKey` não tem como carregar o Stripe.js. A
 * recusa original sobe tal como estava — a compra fica pendente e a tela diz que
 * o banco pediu confirmação —, que é o comportamento honesto de quem não pode
 * autenticar, em vez de um erro técnico sobre uma biblioteca que não carregou.
 */

/** O que o 402 do servidor carrega quando o banco pediu autenticação. */
interface RequiresAction {
	readonly error?: string;
	readonly clientSecret?: string | null;
	readonly chargeRef?: string | null;
	readonly purchaseId?: string | null;
	/** O cartão a reconfirmar com. Ver `StripeJs.confirmCardPayment`. */
	readonly paymentMethodRef?: string | null;
}

export interface GatewayConfig {
	readonly gateway: string;
	readonly publishableKey: string;
	readonly live: boolean;
}

/** `true` quando esta recusa é um 3-D Secure e não um cartão ruim. */
export function needsAuthentication(e: unknown): e is HttpError & { payload: RequiresAction } {
	const payload = (e as { payload?: RequiresAction } | null)?.payload;
	return payload?.error === "CHARGE_REQUIRES_ACTION" && typeof payload.clientSecret === "string";
}

/**
 * Corre o desafio e devolve o que o SERVIDOR apurou depois dele.
 *
 * Atira a recusa ORIGINAL quando não há como autenticar — sem chave, sem
 * biblioteca, sem compra identificável. Quem chamou já sabe desenhar essa
 * recusa; inventar aqui um erro novo sobre um `<script>` seria trocar uma frase
 * que a pessoa entende por uma que não.
 */
export async function finishAuthentication(input: {
	refusal: unknown;
	clientSecret: string;
	purchaseId: string | null;
	paymentMethodRef: string | null;
}): Promise<PackPurchase> {
	let config: GatewayConfig;
	try {
		config = await request<GatewayConfig>("/v3/billing/config");
	} catch {
		throw input.refusal;
	}
	if (!config.publishableKey || !input.purchaseId) throw input.refusal;

	const stripe = await stripeWith(config.publishableKey).catch(() => null);
	if (!stripe) throw input.refusal;

	/**
	 * O desafio corre, e o resultado dele NÃO decide nada — nem quando falha.
	 *
	 * `confirmCardPayment` com o mesmo cartão, e não `handleCardAction`: ver a
	 * nota em `stripe-js.ts`. É o que retoma um PaymentIntent que a Stripe deixou
	 * em `requires_payment_method` ao desistir dele off-session.
	 *
	 * Perguntar ao servidor mesmo depois de um erro do navegador não é zelo:
	 * entre o `handleCardAction` e este ponto o webhook `payment_intent.succeeded`
	 * pode já ter chegado, e confiar no erro do cliente mostraria "não
	 * autorizado" a alguém que acabou de pagar.
	 */
	await stripe
		.confirmCardPayment(input.clientSecret, {
			...(input.paymentMethodRef ? { payment_method: input.paymentMethodRef } : {}),
		})
		.catch(() => null);

	const outcome = await confirm(input.purchaseId).catch(() => null);
	if (outcome?.status === "paid") return outcome.purchase;

	/**
	 * Não entrou. Sobe a recusa ORIGINAL — "o banco pediu para confirmar esta
	 * compra; ela fica em espera" —, que continua verdadeira: a compra está
	 * pendente, nada foi cobrado, e ela pode ainda concluir pelo webhook se a
	 * pessoa autenticar noutro sítio.
	 */
	throw input.refusal;
}

/** Pergunta ao servidor o que aconteceu, e lê a compra como ela ficou. */
async function confirm(purchaseId: string): Promise<{ status: string; purchase: PackPurchase }> {
	const answer = await request<{ status: string }>(
		`/v3/billing/packs/purchases/${encodeURIComponent(purchaseId)}/confirm`,
		{ method: "POST" },
	);
	const history = await request<{ purchases: PackPurchase[] }>("/v3/billing/packs/history");
	const purchase = history.purchases.find((p) => p.id === purchaseId);
	return {
		status: answer.status,
		purchase: purchase ?? ({ id: purchaseId, status: answer.status } as PackPurchase),
	};
}

/**
 * "Autenticação concluída · créditos creditados", num aviso da casa.
 *
 * Num toast e não numa linha de tela: quem compra um pacote pode estar em
 * qualquer uma das telas que vendem um, e o desafio da Stripe cobre a página
 * enquanto corre. O aviso aparece onde a pessoa estiver a olhar quando ele
 * fechar — que é o topo, sempre no mesmo sítio.
 */
export function announceAuthenticated(): void {
	toast.success(i18n.global.t("error.CHARGE_AUTHENTICATED"));
}
